import { SiteExperienceConfig, FeatureFlags, TeamLogoOptions } from '@draftkings/sportsbook-common-contracts';
import {
    buildTeamLogoOptions,
    getCountryFlagByCountryId,
    getLogoUrl,
    getTeamLogoUrlByTeamId
} from '@draftkings/sportsbook-common-utils';
import type * as ContractTypes from '@draftkings/dk-data-layer';

export type GetTeamLogoUrlProps = {
    participant?: ContractTypes.Participant;
    logos: Record<string, string>;
    productConfig: SiteExperienceConfig;
    staticS3Host: string;
    featureFlags: FeatureFlags;
};

/**
 * Gets the URL for a team logo based on configuration and feature flags.
 *
 * - When the Swoosh flag is enabled and the participant is a team, returns a Swoosh logo.
 * - When the flag is enabled but the participant is NOT a team, returns undefined (no fallback).
 * - Otherwise, returns the legacy logo URL from logos.json.
 */
export const getTeamLogoUrl = ({
    participant,
    logos,
    productConfig,
    staticS3Host,
    featureFlags
}: GetTeamLogoUrlProps): string | undefined => {
    if (!participant) {
        return undefined;
    }

    const isSwooshTeamLogosEnabled = featureFlags['RLS-OSB-WEB-IsSwooshTeamLogosEnabled'].LivePage;

    if (isSwooshTeamLogosEnabled) {
        const isTeam = participant.type === 'Team';
        const isNationalTeam = participant.isNationalTeam || false;

        if (!isTeam) {
            return undefined;
        }

        if (isNationalTeam) {
            const options: TeamLogoOptions = buildTeamLogoOptions('sm', 'dark');
            return getCountryFlagByCountryId(productConfig.swooshBaseUrl, participant.countryCode, options)?.toString();
        }

        const teamId = participant.metadata?.rosettaTeamId || participant.id;
        const logoUrl = getTeamLogoUrlByTeamId(
            productConfig.swooshBaseUrl,
            teamId,
            buildTeamLogoOptions('sm', 'dark', participant.venueRole)
        );

        return logoUrl?.toString();
    }

    return getLogoUrl(staticS3Host, logos, participant.name);
};
